import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

const PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai',    label: 'OpenAI (GPT)' },
  { value: 'webhook',   label: 'Custom Webhook' },
];

const DEFAULT_MODELS = {
  anthropic: 'claude-haiku-4-5-20251001',
  openai:    'gpt-4o-mini',
  webhook:   '',
};

const EMPTY_FORM = {
  name: '', description: '', framework: 'custom',
  model_provider: 'anthropic', model_name: '', system_prompt: '', api_key: '', test_endpoint: '',
};

function Agents({ token }) {
  const toast = useToast();
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newAgent, setNewAgent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [configuringId, setConfiguringId] = useState(null);
  const [configForm, setConfigForm] = useState({ model_provider: 'anthropic', model_name: '', system_prompt: '', api_key: '', test_endpoint: '' });
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAgents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/agents`, { headers });
      if (res.ok) setAgents((await res.json()).agents || []);
    } catch (e) {}
  };

  useEffect(() => {
    fetchAgents();
    const i = setInterval(fetchAgents, 5000);
    return () => clearInterval(i);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.model_name) payload.model_name = DEFAULT_MODELS[payload.model_provider] || '';
      const res = await fetch(`${API_URL}/api/protected/agents`, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to register agent'));
      const data = await res.json();
      setNewAgent(data);
      setShowForm(false);
      setForm(EMPTY_FORM);
      fetchAgents();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agent and its history?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/agents/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete agent'));
      if (configuringId === id) setConfiguringId(null);
      fetchAgents();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const openConfigure = (agent) => {
    setConfiguringId(agent.id);
    setConfigForm({
      model_provider: agent.model_provider || 'anthropic',
      model_name:     agent.model_name || '',
      system_prompt:  agent.system_prompt || '',
      api_key:        '',
      test_endpoint:  agent.test_endpoint || '',
    });
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...configForm };
      if (!payload.model_name) payload.model_name = DEFAULT_MODELS[payload.model_provider] || '';
      const res = await fetch(`${API_URL}/api/protected/agents/${configuringId}`, {
        method: 'PATCH', headers, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to save config'));
      toast('Agent configured — simulations will now run automatically', 'success');
      setConfiguringId(null);
      fetchAgents();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const getStatus = (agent) => {
    if (!agent.last_heartbeat) return { cls: 'badge-neutral', dot: 'inactive', label: 'Never connected' };
    const diff = (Date.now() - new Date(agent.last_heartbeat).getTime()) / 1000;
    if (diff < 60)  return { cls: 'badge-success', dot: 'active',   label: 'Active' };
    if (diff < 300) return { cls: 'badge-warning', dot: 'warning',  label: 'Stale' };
    return { cls: 'badge-neutral', dot: 'inactive', label: 'Inactive' };
  };

  const ModelFields = ({ values, onChange, existingKey }) => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label>AI Provider</label>
          <select value={values.model_provider} onChange={e => onChange({ ...values, model_provider: e.target.value, model_name: '' })}>
            {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        {values.model_provider !== 'webhook' && (
          <div className="form-group">
            <label>Model <span className="text-xs text-muted">(optional — uses default if blank)</span></label>
            <input
              value={values.model_name}
              onChange={e => onChange({ ...values, model_name: e.target.value })}
              placeholder={DEFAULT_MODELS[values.model_provider]}
            />
          </div>
        )}
      </div>
      {values.model_provider !== 'webhook' ? (
        <div className="form-group">
          <label>
            API Key {existingKey && <span className="text-xs text-muted">(leave blank to keep existing)</span>}
          </label>
          <input
            type="password"
            value={values.api_key}
            onChange={e => onChange({ ...values, api_key: e.target.value })}
            placeholder={existingKey ? '••••••••••••••••' : values.model_provider === 'anthropic' ? 'sk-ant-api03-...' : 'sk-...'}
          />
          <div className="text-xs text-muted" style={{ marginTop: 4 }}>
            {values.model_provider === 'anthropic'
              ? 'Get your key at console.anthropic.com → API Keys'
              : 'Get your key at platform.openai.com → API Keys'}
          </div>
        </div>
      ) : (
        <div className="form-group">
          <label>Webhook URL</label>
          <input
            value={values.test_endpoint}
            onChange={e => onChange({ ...values, test_endpoint: e.target.value })}
            placeholder="https://your-agent.com/run"
          />
          <div className="text-xs text-muted" style={{ marginTop: 4 }}>
            We POST <code>&#123;"input":"...","system_prompt":"..."&#125;</code> and read <code>&#123;"output":"..."&#125;</code>
          </div>
        </div>
      )}
      <div className="form-group">
        <label>System Prompt <span className="text-xs text-muted">(optional)</span></label>
        <textarea
          value={values.system_prompt}
          onChange={e => onChange({ ...values, system_prompt: e.target.value })}
          placeholder="You are a helpful assistant..."
          rows={3}
          style={{ resize: 'vertical' }}
        />
      </div>
    </>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Agents</div>
          <div className="page-subtitle">Register and manage your AI agents</div>
        </div>
        <button className="btn-primary" onClick={() => { setShowForm(!showForm); setNewAgent(null); setConfiguringId(null); }}>
          {showForm ? 'Cancel' : '+ Register Agent'}
        </button>
      </div>
      <div className="page-body">

        {/* New API key banner */}
        {newAgent && (
          <div className="alert success" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <strong>Agent "{newAgent.name}" registered!</strong>
            <div className="code-block" style={{ marginTop: 8, width: '100%', wordBreak: 'break-all' }}>{newAgent.api_key}</div>
            <div className="text-xs" style={{ marginTop: 4 }}>Save this monitoring key now — you won't see it again. Use it as the <code>X-Agent-Key</code> header.</div>
          </div>
        )}

        {/* Registration form */}
        {showForm && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Register New Agent</div>
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group">
                  <label>Agent Name *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. my-research-bot" required />
                </div>
                <div className="form-group">
                  <label>Framework</label>
                  <select value={form.framework} onChange={e => setForm({ ...form, framework: e.target.value })}>
                    <option value="custom">Custom</option>
                    <optgroup label="Popular Frameworks">
                      <option value="langchain">LangChain</option>
                      <option value="langgraph">LangGraph</option>
                      <option value="llamaindex">LlamaIndex</option>
                      <option value="crewai">CrewAI</option>
                      <option value="autogen">AutoGen</option>
                      <option value="openai-agents">OpenAI Agents SDK</option>
                      <option value="pydantic-ai">Pydantic AI</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="openclaw">OpenClaw</option>
                      <option value="n8n">n8n</option>
                      <option value="flowise">Flowise</option>
                    </optgroup>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What does this agent do?" />
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '20px 0', paddingTop: 20 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>AI Model for Simulations</div>
                <div className="text-xs text-muted" style={{ marginBottom: 16 }}>
                  Used to automatically run test cases against your agent. You can skip this and configure it later.
                </div>
                <ModelFields values={form} onChange={setForm} existingKey={false} />
              </div>

              <button type="submit" className="btn-primary">Register Agent</button>
            </form>
          </div>
        )}

        {/* Agent list */}
        <div className="card">
          {agents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&bull;</div>
              <h3>No agents registered</h3>
              <p>Register your first agent to start monitoring activity and running automated simulations.</p>
              <button className="btn-primary" onClick={() => setShowForm(true)}>Register First Agent</button>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Framework</th>
                  <th>Status</th>
                  <th>Last Seen</th>
                  <th>Simulations</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => {
                  const s = getStatus(agent);
                  return (
                    <React.Fragment key={agent.id}>
                      <tr>
                        <td>
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{agent.name}</div>
                          {agent.description && <div className="text-xs text-muted">{agent.description}</div>}
                          <div className="text-mono text-xs text-muted">{agent.key_preview}</div>
                        </td>
                        <td><span className="badge badge-info">{agent.framework}</span></td>
                        <td><span className={`badge ${s.cls}`}><span className={`status-dot ${s.dot}`} />{s.label}</span></td>
                        <td className="text-sm">{agent.last_heartbeat ? new Date(agent.last_heartbeat).toLocaleString() : 'Never'}</td>
                        <td>
                          {agent.has_api_key
                            ? <span className="badge badge-success">✓ {agent.model_provider}</span>
                            : <span className="badge badge-warning">⚠ No API key</span>}
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button
                            className={`btn-sm ${configuringId === agent.id ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => configuringId === agent.id ? setConfiguringId(null) : openConfigure(agent)}
                          >
                            {configuringId === agent.id ? 'Close' : 'Configure'}
                          </button>
                          <button className="btn-danger btn-sm" onClick={() => handleDelete(agent.id)}>Delete</button>
                        </td>
                      </tr>

                      {/* Inline configure panel */}
                      {configuringId === agent.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', padding: '20px 24px' }}>
                              <div style={{ fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>
                                Configure AI Model — {agent.name}
                              </div>
                              <form onSubmit={handleSaveConfig}>
                                <ModelFields values={configForm} onChange={setConfigForm} existingKey={agent.has_api_key} />
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="submit" className="btn-primary">Save Configuration</button>
                                  <button type="button" className="btn-secondary" onClick={() => setConfiguringId(null)}>Cancel</button>
                                </div>
                              </form>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default Agents;
